/**
 * A live session's transcript (D282, D372), DOM-free so the console and the phone fold the stream the same way and
 * the folding is tested: the person's messages, the agent's words, its tool calls and what a finished turn changed,
 * in order, from a snapshot's turns and then each event of `sessions.subscribe`.
 *
 * Two kinds of stream reach this, and both read right:
 *  - A conversation on a workbench with D372 says each message once it is written, as a `turn` (with its id, its
 *    author, a marker, a question, blocks), beside the `message` (the prompt an agent received) and the `delta`s
 *    (the reply as it arrives) an older workbench already sent. Once a stream has named a turn, a `message` is
 *    redundant and dropped, and a reply drawn from its deltas is replaced by its turn when that comes.
 *  - An agent session of its own, or an older workbench, sends only `message` and `delta`: the transcript is those.
 *
 * Every field the stream adds is optional: an item carries what the event carried and nothing is required of it.
 */
import { LIVE_CHECKPOINT_PATTERN } from '../schemas/remoteControl.js';
import { groupToolRuns, toolRunsByAgent } from '../ui/liveSessionParts.js';
export const EMPTY_TRANSCRIPT = { items: [], turns: false };
const MESSAGE_ROLES = new Set(['user', 'assistant', 'system']);
/** One turn as a message item, with whatever it carries. */
export function messageOfTurn(t, key) {
    return {
        kind: 'message',
        key,
        role: t.role,
        text: typeof t.text === 'string' ? t.text : '',
        at: t.at,
        ...(t.id ? { id: t.id } : {}),
        ...(t.author?.name ? { author: t.author } : {}),
        ...(t.marker?.kind ? { marker: t.marker } : {}),
        ...(t.asks?.questionId ? { asks: t.asks } : {}),
        ...(t.intent ? { intent: t.intent } : {}),
        ...(t.questionId ? { questionId: t.questionId } : {}),
        ...(Array.isArray(t.blocks) && t.blocks.length ? { blocks: t.blocks } : {}),
    };
}
/**
 * A snapshot's turns, in order. A role this view does not know is left out, that turn alone. `keyOf` names each
 * item's key (the console and the phone key their rows differently).
 */
export function transcriptFromSnapshot(turns, keyOf = (i) => `snap-${i}`) {
    const list = (turns ?? []).filter((t) => t && MESSAGE_ROLES.has(t.role));
    return { items: list.map((t, i) => messageOfTurn(t, keyOf(i))), turns: list.some((t) => Boolean(t.id)) };
}
const sameAuthor = (a, b) => !a?.name || !b?.name || a.name === b.name;
/** Where the reply being written starts: after the last message a turn named. */
function lastNamed(items) {
    for (let i = items.length - 1; i >= 0; i--) {
        const it = items[i];
        if (it.kind === 'message' && it.id && !it.draft)
            return i;
    }
    return -1;
}
function applyTurn(items, turn) {
    // The same message again (it was edited, or a resync overlapped): in place.
    if (turn.id) {
        const at = items.findIndex((x) => x.kind === 'message' && x.id === turn.id);
        if (at >= 0)
            return items.map((x, i) => (i === at ? { ...turn, key: x.key } : x));
    }
    const from = lastNamed(items) + 1;
    if (turn.role === 'user') {
        // The prompt as the agent received it (`message`) said this already, when it came first.
        return [...items.filter((x, i) => !(i >= from && x.kind === 'message' && x.role === 'user' && !x.id)), turn];
    }
    if (turn.role === 'assistant') {
        // The reply drawn from its deltas: the turn takes the place of the last of them, the rest go.
        const drafts = items.map((x, i) => (i >= from && x.kind === 'message' && x.role === 'assistant' && x.draft && sameAuthor(x.author, turn.author) ? i : -1)).filter((i) => i >= 0);
        if (drafts.length) {
            const last = drafts[drafts.length - 1];
            const gone = new Set(drafts);
            const out = [];
            items.forEach((x, i) => {
                if (i === last)
                    out.push({ ...turn, key: x.key });
                else if (!gone.has(i))
                    out.push(x);
            });
            return out;
        }
    }
    return [...items, turn];
}
/** One event applied in order; the kinds that are not the transcript's (status, usage, approvals …) change nothing. */
export function foldTranscript(t, e, key) {
    switch (e.kind) {
        case 'message':
            if (t.turns)
                return t;
            return { ...t, items: [...t.items, { kind: 'message', key, role: 'user', text: e.text, at: e.at, ...(e.via ? { via: e.via } : {}) }] };
        case 'delta': {
            const last = t.items.at(-1);
            const author = e.author?.name ? { name: e.author.name } : undefined;
            // The same speaker's reply goes on; another member speaking starts their own.
            if (last?.kind === 'message' && last.role === 'assistant' && last.draft && (last.author?.name ?? '') === (author?.name ?? '')) {
                return { ...t, items: [...t.items.slice(0, -1), { ...last, text: last.text + e.text }] };
            }
            return { ...t, items: [...t.items, { kind: 'message', key, role: 'assistant', text: e.text, at: e.at, draft: true, ...(author ? { author } : {}) }] };
        }
        case 'turn': {
            if (!MESSAGE_ROLES.has(e.role))
                return t;
            const { seq: _s, at, kind: _k, sessionId: _sid, conversationId: _cid, ...rest } = e;
            return { turns: true, items: applyTurn(t.items, messageOfTurn({ ...rest, at }, key)) };
        }
        case 'tool': {
            const i = e.toolCallId ? t.items.findIndex((x) => x.kind === 'tool' && x.toolCallId === e.toolCallId) : -1;
            const fields = { title: e.title, toolKind: e.toolKind, state: e.state, riskLevel: e.riskLevel, detail: e.detail, agent: e.agent, failed: e.failed };
            const set = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
            if (i >= 0)
                return { ...t, items: t.items.map((x, n) => (n === i ? { ...x, ...set } : x)) };
            return { ...t, items: [...t.items, { kind: 'tool', key, ...(e.toolCallId ? { toolCallId: e.toolCallId } : {}), ...set, at: e.at }] };
        }
        case 'changes': {
            if (!e.turnId || !Array.isArray(e.files) || !e.files.length)
                return t;
            // A checkpoint is kept only in the shape git.diff takes, and only with the repository it is in.
            const checkpoint = typeof e.checkpoint === 'string' && LIVE_CHECKPOINT_PATTERN.test(e.checkpoint) && typeof e.rootId === 'string' && e.rootId ? { checkpoint: e.checkpoint, rootId: e.rootId } : {};
            const item = { kind: 'changes', key, turnId: e.turnId, files: e.files, at: e.at, ...checkpoint };
            const i = t.items.findIndex((x) => x.kind === 'changes' && x.turnId === e.turnId);
            return { ...t, items: i >= 0 ? t.items.map((x, n) => (n === i ? { ...item, key: x.key } : x)) : [...t.items, item] };
        }
        default:
            return t;
    }
}
/**
 * The same fold over a list that also holds a client's own rows (the phone keeps an approval and a note in line with
 * the transcript): those rows are never read or changed, only kept in their place.
 */
export function foldTranscriptAmong(t, e, key) {
    return foldTranscript({ items: t.items, turns: t.turns }, e, key);
}
/** The answer each question has had in the transcript, by its question id: a later message that answers it. */
export function answersByQuestion(items) {
    const out = new Map();
    for (const it of items)
        if (it.kind === 'message' && it.intent === 'answer' && it.questionId && !out.has(it.questionId))
            out.set(it.questionId, it);
    return out;
}
/** The questions still waiting for an answer, oldest first. */
export function openQuestions(items) {
    const answered = answersByQuestion(items);
    return items.filter((x) => x.kind === 'message' && Boolean(x.asks) && !answered.has(x.asks.questionId));
}
/**
 * The transcript as the workbench draws it: each run of tool calls between what is said taken together, one group per
 * agent where the calls name theirs, and an answer drawn under its question rather than on its own.
 */
export function liveRows(items) {
    const asked = new Set(items.flatMap((x) => (x.kind === 'message' && x.asks ? [x.asks.questionId] : [])));
    const shown = items.filter((x) => !(x.kind === 'message' && x.intent === 'answer' && x.questionId && asked.has(x.questionId)));
    const out = [];
    for (const row of groupToolRuns(shown, (x) => x.kind === 'tool', (x) => x.key)) {
        if (row.kind === 'item') {
            out.push({ kind: 'item', item: row.item });
            continue;
        }
        const groups = toolRunsByAgent(row.items);
        groups.forEach((g, i) => out.push({ kind: 'tools', key: i ? `${row.key}:${i}` : row.key, ...(g.agent ? { agent: g.agent } : {}), items: g.items }));
    }
    return out;
}
/**
 * Who wrote a message, in words: the person's own ("You", or the name the page gives them), a teammate's by their
 * name, the member speaking, or the workbench. `me` is the signed-in person's id, to tell their own messages.
 */
export function authorWords(m, person, me) {
    const name = m.author?.name?.trim();
    if (m.role === 'system')
        return name || 'The workbench';
    if (m.role === 'user')
        return name && !(me && m.author?.id === me) ? name : person;
    return name || 'The agent';
}
//# sourceMappingURL=liveTranscript.js.map